import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface EscapeEntry {
    depth: number;
    escapable: () => boolean;
    close: () => void;
}

const escapeStack: EscapeEntry[] = [];
let nextModalDepth = 1;

export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    size?: ModalSize;
    closeOnBackdropClick?: boolean;
    /**
     * Defaults to `closeOnBackdropClick`: a modal that guards its contents
     * against a stray click (an editor, a call wrap-up) guards them against a
     * stray Escape too.
     */
    closeOnEscape?: boolean;
    className?: string;
    overlayClassName?: string;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
};

interface ModalHeaderProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    onClose?: () => void;
    actions?: React.ReactNode;
    className?: string;
}

interface ModalBodyProps {
    children: React.ReactNode;
    className?: string;
}

interface ModalFooterProps {
    children: React.ReactNode;
    className?: string;
}

function Modal({
    isOpen,
    onClose,
    children,
    size = "lg",
    closeOnBackdropClick = true,
    closeOnEscape = closeOnBackdropClick,
    className = "",
    overlayClassName = "",
}: ModalProps) {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const escapableRef = useRef(closeOnEscape);
    escapableRef.current = closeOnEscape;
    // Render walks parents before children, and passive effects run the other
    // way. Depth assigned here keeps Escape on the modal that was opened
    // inside another, rather than on whichever effect registered last.
    const depthRef = useRef(0);
    if (!isOpen) depthRef.current = 0;
    else if (depthRef.current === 0) depthRef.current = nextModalDepth++;

    useEffect(() => {
        if (!isOpen) return;
        const entry: EscapeEntry = {
            depth: depthRef.current,
            escapable: () => escapableRef.current,
            close: () => onCloseRef.current(),
        };
        escapeStack.push(entry);
        // Bubble phase, so a dropdown or field inside the modal that handles
        // Escape itself (and stops or prevents it) is served first.
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== "Escape" || event.defaultPrevented || escapeStack.length === 0) return;
            const top = escapeStack.reduce((best, item) => (item.depth > best.depth ? item : best));
            if (top !== entry) return;
            // Swallowed even when this modal stays open, so nothing beneath it
            // (another modal, a drawer) closes instead.
            event.stopImmediatePropagation();
            if (!entry.escapable()) return;
            event.preventDefault();
            entry.close();
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("keydown", onKey);
            const index = escapeStack.indexOf(entry);
            if (index >= 0) escapeStack.splice(index, 1);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className={`fixed inset-0 bg-brand-ink/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 ${overlayClassName}`}
            onClick={closeOnBackdropClick ? onClose : undefined}
        >
            <div
                className={`bg-white rounded-panel shadow-panel border border-brand-line w-full ${SIZE_CLASSES[size]} max-h-[90vh] flex flex-col overflow-hidden ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body
    );
}

function ModalHeader({ title, subtitle, onClose, actions, className = "" }: ModalHeaderProps) {
    const { t } = useTranslation("common");
    return (
        <div className={`px-6 py-4 border-b border-brand-line flex items-start justify-between gap-4 flex-shrink-0 ${className}`}>
            <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-brand-ink truncate">{title}</h3>
                {subtitle && <div className="text-sm text-brand-ink/60 mt-0.5">{subtitle}</div>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                {actions}
                {onClose && (
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-button text-brand-ink/40 hover:text-brand-ink hover:bg-brand-cream transition-colors duration-150"
                        title={t("actions.close")}
                        aria-label={t("actions.close")}
                        type="button"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                )}
            </div>
        </div>
    );
}

function ModalBody({ children, className = "" }: ModalBodyProps) {
    return <div className={`flex-1 overflow-y-auto p-6 ${className}`}>{children}</div>;
}

function ModalFooter({ children, className = "" }: ModalFooterProps) {
    return <div className={`px-6 py-3 border-t border-brand-line bg-brand-cream/40 flex items-center justify-end gap-2 flex-shrink-0 ${className}`}>{children}</div>;
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export { Modal };
export default Modal;
